import { React, useEffect, useState } from "react";
import ReactPaginate from "react-paginate";
import axios from "axios";
import PropTypes from "prop-types";
import { ResearchPositionEdit } from "..";
import { Star as StarIcon, Room as RoomIcon, StarBorder as StarBorderIcon, Close as CloseIcon } from "@mui/icons-material";

function ResearchPositionTable(props) {
  const {
    researchPositions,
    selectedResearchPosition,
    setResearchPosition,
    userType,
    pid,
    updateFavorites,
    setUpdateFavorites,
    updateTable,
    setUpdateTable,
    updateOffset,
    editedTable,
    setEditedTable,
    currentPage,
    setCurrentPage,
    itemOffset,
    setItemOffset,
    setPositionOffset,
    handleChange,
    setHandleChange
  } = props;

  const handleResearchPositionFavorited = (positionId) => {
    const body = new FormData();
    body.append("positionId", positionId);
    body.append("pid", pid);
    axios.post("/faculty/favoriteResearchPosition", body).then((res) => {
      const data = res.data;
      if (data.success) {
        setUpdateFavorites(!updateFavorites);
        setHandleChange(true);
        setPositionOffset(positionId);
      }
    });
  };

  const handleDelete = (positionId) => {
    if (confirm("Are you sure you would like to delete this position? In doing so, you will be deleting all associated student applications with this position.")) {
      const body = new FormData();
      body.append("positionId", positionId);
      axios.post("/faculty/deleteResearchPosition", body).then((res) => {
        const data = res.data;
        if (data.success) {
          setUpdateTable(!updateTable);
        }
      });
    };
  };

  // Here we use item offsets; we could also use page offsets
  // following the API or data you're working with.

  useEffect(() => {
    setCurrentPage(currentPage);
    setItemOffset(itemOffset);
  }, [researchPositions]);

  useEffect(() => {
    if (!handleChange) {
      setItemOffset(0);
    }
    else {
      setHandleChange(false);
    }

  }, [updateOffset]);

  const items = researchPositions.map((researchPosition, index) => (
    <div
      className={"ResearchPositionButton-container flex p-2 rounded-md border-2 drop-shadow-2xl mb-2" + (researchPosition.positionId === selectedResearchPosition.positionId ? " border-brand-primary" : "")}
      key={index}
    >
      <div className="ResearchPositionButton-leftContainer w-full flex flex-col">
        <button
          type="button"
          className="w-full p-0 capitalize border-b last:border-b-0"
          onClick={() => setResearchPosition(researchPosition)}
        >
          <div className="ResearchPositionButton-compensation text-base font-brand-primary flex mb-2">
            {Array.isArray(researchPosition.researchPositionCompensation) ?
              researchPosition.researchPositionCompensation.join(", ") :
              researchPosition.researchPositionCompensation}
          </div>
          <div className="ResearchPositionButton-title text-xl font-bold font-brand-primary text-start flex mb-2">
            {researchPosition.title}
          </div>
          <div className="ResearchPositionButton-compensation text-base font-brand-primary flex mb-2">
            {Array.isArray(researchPosition.researchPositionType) ? researchPosition.researchPositionType.join(", ") : researchPosition.researchPositionType}
          </div>
          <div className="ResearchPositionButton-location text-base mb-3 flex justify-start py-auto">
            <RoomIcon fontSize="medium" className="flex my-auto mr-1" />
            {(researchPosition.location != "undefined") && (
              <div className="text-start">{researchPosition.location}</div>
            )}
          </div>
          <div className="ResearchPositionButton-faculty text-base font-brand-primary flex">
            {researchPosition.facultyName}
          </div>
          {(userType === "faculty" || userType === "admin") && (
            <div className="ResearchPositionButton-viewCount text-base font-brand-primary flex">
              View Count: {researchPosition.viewCount}
            </div>
          )}
          {(userType === "faculty" || userType === "admin") && (
            <div className="ResearchPositionButton-applyCount text-base font-brand-primary flex">
              Number Applied: {researchPosition.applyCount}
            </div>
          )}
        </button>
      </div>
      <div className="StudentButton-rightContainer">
        {userType === "student" && (
          <button
            className="flex"
            onClick={() => handleResearchPositionFavorited(researchPosition.positionId)}
          >
            {researchPosition.favorited === "true" ? (
              <StarIcon fontSize="large" className="fill-yellow-400" />
            ) : (
              <StarBorderIcon fontSize="large" className="fill-yellow-400" />
            )}
          </button>
        )}
        {(userType === "admin" || userType === "faculty") && (
          <button className="flex" onClick={() => handleDelete(researchPosition.positionId)}>
            <CloseIcon fontSize="large" className="fill-brand-primary" />
          </button>
        )}
        {(userType === "admin" || userType === "faculty") && (
          <ResearchPositionEdit
            userType={userType}
            pid={pid}
            updateTable={editedTable}
            setUpdateTable={setEditedTable}
            researchPosition={researchPosition}
          />
        )}
      </div>
    </div>
  ));

  const itemsPerPage = 5;
  const endOffset = itemOffset + itemsPerPage;
  const currentItems = items.slice(itemOffset, endOffset);
  const pageCount = Math.ceil(items.length / itemsPerPage);

  // Invoke when user click to request another page.
  const handlePageClick = (event) => {
    const newOffset = (event.selected * itemsPerPage) % items.length;
    setItemOffset(newOffset);
    setCurrentPage(event.selected);
    if (researchPositions.length > newOffset) {
      setResearchPosition(researchPositions[newOffset]);
    }
  };

  if (researchPositions.length === 0) {
    return (
      <div className="ResearchPositionTable bg-white p-2 mr-4 rounded-xl h-5/6">
        No research positions found.
      </div>
    );
  } else {
    return (
      <div className="ResearchPositionTable bg-white p-2 mr-4 rounded-xl h-full" style={{ minHeight: "816px" }}>
        <ReactPaginate
          forcePage={currentPage}
          breakLabel="..."
          nextLabel=">"
          onPageChange={handlePageClick}
          pageRangeDisplayed={3}
          pageCount={pageCount}
          previousLabel="<"
          renderOnZeroPageCount={null}
          className="flex w-full justify-between mb-6 mt-4 [&>li>a]:py-4 [&>li>a]:px-6 [&>li>a]:border [&>li>a]:rounded-lg [&>li.selected>a]:border-brand-primary"
        />
        <div className="ResearchPositionTable-currentResearchPositions overflow-y-auto">
          {currentItems}
        </div>
      </div>
    );
  }
}

ResearchPositionTable.propTypes = {
  researchPositions: PropTypes.arrayOf(PropTypes.shape({
    applyCount: PropTypes.number,
    applyDetails: PropTypes.string,
    faculty: PropTypes.string,
    facultyLink: PropTypes.string,
    details: PropTypes.string,
    draft: PropTypes.bool,
    endDate: PropTypes.string,
    favorited: PropTypes.string,
    positionId: PropTypes.number,
    researchPositionResearchArea: PropTypes.arrayOf(PropTypes.string),
    researchPositionCompensation: PropTypes.arrayOf(PropTypes.string),
    researchPositionType: PropTypes.arrayOf(PropTypes.string),
    link: PropTypes.string,
    location: PropTypes.string,
    skills: PropTypes.arrayOf(PropTypes.string),
    startDate: PropTypes.string,
    title: PropTypes.string,
    pid: PropTypes.string,
    viewCount: PropTypes.number,
  })),
  selectedResearchPosition: PropTypes.shape({
    title: PropTypes.string,
    location: PropTypes.string,
    faculty: PropTypes.string,
    details: PropTypes.string,
    link: PropTypes.string,
    facultyLink: PropTypes.string,
    applyDetails: PropTypes.string,
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    researchPositionType: PropTypes.arrayOf(PropTypes.string),
    researchPositionCompensation: PropTypes.arrayOf(PropTypes.string),
    researchPositionResearchArea: PropTypes.arrayOf(PropTypes.string),
    skills: PropTypes.arrayOf(PropTypes.string),
    location: PropTypes.string,
    viewCount: PropTypes.number,
    applyCount: PropTypes.number,
    favorited: PropTypes.string,
    positionId: PropTypes.number,
  }),
  setResearchPosition: PropTypes.func,
  onResearchPositionApplied: PropTypes.func,
  userType: PropTypes.string,
  pid: PropTypes.string,
  updateFavorites: PropTypes.bool,
  setUpdateFavorites: PropTypes.func,
  updateTable: PropTypes.bool,
  setUpdateTable: PropTypes.func,
  updateOffset: PropTypes.bool,
  editedTable: PropTypes.bool,
  setEditedTable: PropTypes.func,
  currentPage: PropTypes.string,
  setCurrentPage: PropTypes.func,
  itemOffset: PropTypes.string,
  setItemOffset: PropTypes.func,
  setPositionOffset: PropTypes.func,
  handleChange: PropTypes.bool,
  setHandleChange: PropTypes.func
};

ResearchPositionTable.defaultProps = {
  researchPositions: [],
  selectedResearchPosition: {},
  setResearchPosition: () => { },
  onResearchPositionApplied: () => { },
  userType: "",
  pid: "",
  updateFavorites: false,
  setUpdateFavorites: () => { },
  updateTable: false,
  setUpdateTable: () => { },
  updateOffset: false,
  editedTable: false,
  setEditedTable: () => { },
  currentPage: "",
  setCurrentPage: () => { },
  itemOffset: "",
  setItemOffset: () => { },
  setPositionOffset: () => { },
  handleChange: false,
  setHandleChange: () => { }
};

export default ResearchPositionTable;
